import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { HiOutlineCurrencyDollar, HiOutlineStar, HiOutlineChartBar, HiOutlineRocketLaunch } from 'react-icons/hi2';
import './HomePage.css';

// Video for hero section
import heroVideo from '../../assets/luxury-yacht-video.mp4';

// Images - Replace these with your own images
import heroImage from '../../assets/luxury-yacht2.jpg';
import showcaseImage1 from '../../assets/luxury-yacht.jpg';
import showcaseImage2 from '../../assets/luxury-yacht3.jpg';
import showcaseImage3 from '../../assets/helicopter2.jpg';
import experienceImage from '../../assets/helicopter.jpg';
import ctaImage from '../../assets/cta.jpg';

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isApproved, isPending, hasApplied, affiliateTier } = useAuth();
  const [scrollY, setScrollY] = useState(0);
  const [horizontalProgress, setHorizontalProgress] = useState(0);
  const heroRef = useRef(null);
  const showcaseRef = useRef(null);
  const numSlides = 3;

  // Parallax scroll effect and horizontal scroll calculation
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      
      // Calculate horizontal scroll progress for showcase section
      if (showcaseRef.current) {
        const rect = showcaseRef.current.getBoundingClientRect();
        const sectionTop = rect.top;
        const sectionHeight = showcaseRef.current.offsetHeight;
        const windowHeight = window.innerHeight;
        
        // The scrollable distance is section height minus one viewport height
        const scrollableDistance = sectionHeight - windowHeight;
        
        if (sectionTop <= 0 && sectionTop > -scrollableDistance) {
          // We're in the sticky zone - calculate progress
          const scrolled = Math.abs(sectionTop);
          const progress = scrolled / scrollableDistance;
          setHorizontalProgress(Math.min(Math.max(progress, 0), 1));
        } else if (sectionTop > 0) {
          // Haven't reached the section yet
          setHorizontalProgress(0);
        } else {
          // Past the section
          setHorizontalProgress(1);
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const animatedElements = document.querySelectorAll('.scroll-animate');
    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: <HiOutlineCurrencyDollar />,
      title: 'Lucrative Commissions',
      description: 'Earn up to 25% on every successful booking through your unique affiliate link.',
      delay: 0
    },
    {
      icon: <HiOutlineStar />,
      title: 'Exclusive Access',
      description: 'Book premium experiences at up to 100% discount as a top-tier affiliate.',
      delay: 100
    },
    {
      icon: <HiOutlineChartBar />,
      title: 'Real-Time Analytics',
      description: 'Track your performance, earnings, and conversions with our powerful dashboard.',
      delay: 200
    },
    {
      icon: <HiOutlineRocketLaunch />,
      title: 'Growth System',
      description: 'Progress through 5 tiers to unlock increasingly better rewards and benefits.',
      delay: 300
    }
  ];

  const tiers = [
    { level: 1, name: 'Bronze', discount: '20%', commission: '5%', color: '#CD7F32' },
    { level: 2, name: 'Silver', discount: '40%', commission: '8%', color: '#C0C0C0' },
    { level: 3, name: 'Gold', discount: '60%', commission: '10%', color: '#FFD700' },
    { level: 4, name: 'Platinum', discount: '80%', commission: '12%', color: '#E5E4E2' },
    { level: 5, name: 'Diamond', discount: '100%', commission: '15%', color: '#B9F2FF' }
  ];

  const steps = [
    { number: '01', title: 'Apply', description: 'Create your account and submit your application' },
    { number: '02', title: 'Get Approved', description: 'Our team reviews and assigns your tier' },
    { number: '03', title: 'Share & Earn', description: 'Start promoting and earning commissions' }
  ];

  return (
    <Box className="premium-homepage">
      {/* Hero Section with Video */}
      <section className="hero-section" ref={heroRef}>
        {/* Hero Background Video */}
        <div className="hero-video-container">
          <video 
            className="hero-video"
            autoPlay 
            muted 
            loop 
            playsInline
            style={{ transform: `translateY(${scrollY * 0.3}px) scale(1.1)` }}
          >
            <source src={heroVideo} type="video/mp4" />
          </video>
        </div>
        <div className="hero-bg-overlay" />
        <div 
          className="hero-bg-pattern"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        />
        
        {/* Floating Elements */}
        <div className="floating-elements">
          <div className="float-circle float-1" style={{ transform: `translateY(${scrollY * 0.2}px)` }} />
          <div className="float-circle float-2" style={{ transform: `translateY(${scrollY * -0.15}px)` }} />
          <div className="float-circle float-3" style={{ transform: `translateY(${scrollY * 0.25}px)` }} />
        </div>

        <div className="hero-content">
          <div className="hero-badge animate-fade-in">
            <span className="badge-dot" />
            Exclusive Partner Program
          </div>
          
          <h1 className="hero-title animate-fade-in-up">
            Elevate Your <span className="gradient-text">Earnings</span>
            <br />With Luxury Experiences
          </h1>
          
          <p className="hero-subtitle animate-fade-in-up delay-1">
            Partner with Bosphorus Yachts and unlock premium commissions, 
            exclusive discounts, and access to world-class maritime experiences.
          </p>

          <div className="hero-cta animate-fade-in-up delay-2">
            {!isAuthenticated ? (
              <>
                <Button className="btn-primary" onClick={() => navigate('/signup')}>
                  Become a Partner
                  <span className="btn-arrow">→</span>
                </Button>
                <Button className="btn-secondary" onClick={() => navigate('/login')}>
                  Sign In
                </Button>
              </>
            ) : isApproved ? (
              <>
                <Button className="btn-primary" onClick={() => navigate('/dashboard')}>
                  View Dashboard
                  <span className="btn-arrow">→</span>
                </Button>
                <Button className="btn-secondary" onClick={() => navigate('/experiences')}>
                  Browse Experiences
                </Button>
              </>
            ) : isPending ? (
              <div className="pending-status">
                <div className="pending-icon">⏳</div>
                <div className="pending-text">
                  <strong>Application Under Review</strong>
                  <span>We'll notify you once approved</span>
                </div>
              </div>
            ) : (
              <>
                <Button className="btn-primary" onClick={() => navigate('/profile')}>
                  {hasApplied ? 'View Profile' : 'Complete Application'}
                  <span className="btn-arrow">→</span>
                </Button>
                <Button className="btn-secondary" onClick={() => navigate('/experiences')}>
                  Preview Experiences
                </Button>
              </>
            )}
          </div>

          {isApproved && affiliateTier && (
            <div className="tier-indicator animate-fade-in-up delay-3">
              <span className="tier-label">Your Status</span>
              <span className="tier-value" style={{ color: tiers[affiliateTier - 1]?.color }}>
                {tiers[affiliateTier - 1]?.name} Partner
              </span>
            </div>
          )}

          {/* Stats Bar */}
          {/* <div className="hero-stats animate-fade-in-up delay-3">
            <div className="stat-item">
              <span className="stat-number">500+</span>
              <span className="stat-label">Active Partners</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-number">€2M+</span>
              <span className="stat-label">Commissions Paid</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-number">15%</span>
              <span className="stat-label">Max Commission</span>
            </div>
          </div> */}

          <div className="scroll-indicator">
            <span>Scroll to explore</span>
            <div className="scroll-line" />
          </div>
        </div>
      </section>

      {/* Hero Image Section */}
      <section className="hero-image-section">
        <div 
          className="hero-image-bg"
          style={{ 
            backgroundImage: `url(${heroImage})`,
            transform: `translateY(${Math.max(0, (scrollY - 400) * 0.15)}px)`
          }}
        />
        <div className="hero-image-overlay" />
        <div className="hero-image-content scroll-animate">
          <span className="section-tag">Discover Excellence</span>
          <h2 className="hero-image-title">
            Luxury Awaits on the <span className="highlight">Bosphorus</span>
          </h2>
          <p className="hero-image-description">
            Partner with us to share unforgettable yacht experiences with your audience 
            and earn exceptional commissions on every booking.
          </p>
        </div>
      </section>

      {/* Horizontal Scroll Showcase Section */}
      <section className="showcase-section" ref={showcaseRef}>
        <div className="showcase-sticky">
          <div 
            className="showcase-track"
            style={{ 
              transform: `translateX(${-horizontalProgress * (numSlides - 1) * 100}vw)`
            }}
          >
            {/* Slide 1 */}
            <div className="showcase-slide">
              <div 
                className="showcase-slide-bg"
                style={{ backgroundImage: `url(${showcaseImage1})` }}
              />
              <div className="showcase-slide-overlay" />
              <div className="showcase-slide-content">
                <span className="showcase-slide-number">01</span>
                <span className="showcase-slide-tag">Premium Experiences</span>
                <h2 className="showcase-slide-title">Luxury Yacht Tours</h2>
                <p className="showcase-slide-description">
                  Exclusive access to world-class maritime adventures on the stunning Bosphorus waters
                </p>
              </div>
            </div>

            {/* Slide 2 */}
            <div className="showcase-slide">
              <div 
                className="showcase-slide-bg"
                style={{ backgroundImage: `url(${showcaseImage2})` }}
              />
              <div className="showcase-slide-overlay" />
              <div className="showcase-slide-content">
                <span className="showcase-slide-number">02</span>
                <span className="showcase-slide-tag">Unforgettable Moments</span>
                <h2 className="showcase-slide-title">Sunset Cruises</h2>
                <p className="showcase-slide-description">
                  Watch the golden sun dip below the horizon while cruising through ancient waterways
                </p>
              </div>
            </div>

            {/* Slide 3 */}
            <div className="showcase-slide">
              <div 
                className="showcase-slide-bg"
                style={{ backgroundImage: `url(${showcaseImage3})` }}
              />
              <div className="showcase-slide-overlay" />
              <div className="showcase-slide-content">
                <span className="showcase-slide-number">03</span>
                <span className="showcase-slide-tag">Exclusive Access</span>
                <h2 className="showcase-slide-title">Private Charters</h2>
                <p className="showcase-slide-description">
                  Bespoke experiences tailored to your clients' every desire and celebration
                </p>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="showcase-progress">
            <div className="showcase-progress-bar">
              <div 
                className="showcase-progress-fill"
                style={{ width: `${horizontalProgress * 100}%` }}
              />
            </div>
            <span className="showcase-progress-text">
              {String(Math.floor(horizontalProgress * numSlides) + 1).padStart(2, '0')} / {String(numSlides).padStart(2, '0')}
            </span>
          </div>

          {/* Scroll Hint */}
          <div className="showcase-scroll-hint" style={{ opacity: horizontalProgress < 0.9 ? 1 : 0 }}>
            <span>Keep scrolling</span>
            <div className="scroll-arrow">→</div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-container">
          <div className="section-header scroll-animate">
            <span className="section-tag">Why Partner With Us</span>
            <h2 className="section-title">
              Built for <span className="highlight">Success</span>
            </h2>
            <p className="section-description">
              Everything you need to maximize your earning potential
            </p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="feature-card scroll-animate"
                style={{ transitionDelay: `${feature.delay}ms` }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                <div className="feature-glow" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tiers Section */}
      {/* <section className="tiers-section">
        <div className="tiers-bg" />
        <div className="section-container">
          <div className="section-header scroll-animate">
            <span className="section-tag">Partner Tiers</span>
            <h2 className="section-title">
              Unlock <span className="highlight">Premium</span> Benefits
            </h2>
            <p className="section-description">
              Progress through our tier system to access exclusive rewards
            </p>
          </div>

          <div className="tiers-showcase scroll-animate">
            {tiers.map((tier, index) => (
              <div 
                key={tier.level}
                className={`tier-card ${affiliateTier === tier.level ? 'active' : ''}`}
                style={{ 
                  '--tier-color': tier.color,
                  transitionDelay: `${index * 100}ms`
                }}
              >
                <div className="tier-glow" style={{ background: tier.color }} />
                <div className="tier-header">
                  <span className="tier-level">Tier {tier.level}</span>
                  <span className="tier-name" style={{ color: tier.color }}>{tier.name}</span>
                </div>
                <div className="tier-benefits">
                  <div className="tier-benefit">
                    <span className="benefit-value">{tier.discount}</span>
                    <span className="benefit-label">Discount</span>
                  </div>
                  <div className="tier-benefit">
                    <span className="benefit-value">{tier.commission}</span>
                    <span className="benefit-label">Commission</span>
                  </div>
                </div>
                {affiliateTier === tier.level && (
                  <div className="current-badge">Current</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Experience Highlight Section */}
      <section className="experience-highlight-section">
        <div className="section-container">
          {/* Top Row: Image + Title & Features */}
          <div className="experience-highlight-grid">
            <div className="experience-image-wrapper scroll-animate">
              <div 
                className="experience-image"
                style={{ 
                  backgroundImage: `url(${experienceImage})`
                }}
              />
              <div className="experience-image-frame" />
              <div className="experience-image-accent" />
            </div>
            <div className="experience-content-top scroll-animate" style={{ transitionDelay: '200ms' }}>
              <h2 className="section-title" style={{ textAlign: 'left' }}>
                Share <span className="highlight">Unforgettable</span> Moments
              </h2>
              <ul className="experience-features">
                <li>
                  <span className="check-icon">✓</span>
                  Curated luxury yacht experiences
                </li>
                <li>
                  <span className="check-icon">✓</span>
                  Professional crew and services
                </li>
                <li>
                  <span className="check-icon">✓</span>
                  Unforgettable sunset tours
                </li>
                <li>
                  <span className="check-icon">✓</span>
                  Private celebrations & events
                </li>
              </ul>
            </div>
          </div>
          
          {/* Bottom Row: Tag, Description & Button */}
          <div className="experience-bottom-content scroll-animate">
            <span className="section-tag">The Experience</span>
            <p className="experience-text">
              As an affiliate partner, you're not just sharing links — you're connecting 
              people with extraordinary experiences on the beautiful Bosphorus waters.
            </p>
            <Button className="btn-primary" onClick={() => navigate('/experiences')}>
              Explore Experiences
              <span className="btn-arrow">→</span>
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="process-section">
        <div className="section-container">
          <div className="section-header scroll-animate">
            <span className="section-tag">Getting Started</span>
            <h2 className="section-title">
              Three Simple <span className="highlight">Steps</span>
            </h2>
          </div>

          <div className="process-timeline scroll-animate">
            {steps.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{step.number}</div>
                <div className="step-content">
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
                {index < steps.length - 1 && <div className="step-connector" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div 
          className="cta-bg-image"
          style={{ 
            backgroundImage: `url(${ctaImage})`
          }}
        />
        <div className="cta-bg-glow" />
        <div className="section-container">
          <div className="cta-content scroll-animate">
            <h2 className="cta-title">
              Ready to Start Your
              <span className="gradient-text"> Journey?</span>
            </h2>
            <p className="cta-description">
              Join our exclusive network of partners and start earning today.
            </p>
            {!isAuthenticated ? (
              <Button className="btn-primary btn-large" onClick={() => navigate('/signup')}>
                Get Started Now
                <span className="btn-arrow">→</span>
              </Button>
            ) : !isApproved && !hasApplied ? (
              <Button className="btn-primary btn-large" onClick={() => navigate('/profile')}>
                Complete Application
                <span className="btn-arrow">→</span>
              </Button>
            ) : isApproved ? (
              <Button className="btn-primary btn-large" onClick={() => navigate('/experiences')}>
                Explore Experiences
                <span className="btn-arrow">→</span>
              </Button>
            ) : null}
          </div>
        </div>
      </section>
    </Box>
  );
};

export default HomePage;
